import { Form } from '@ant-design/compatible';
import { PageHeader, Input, Select, InputNumber } from 'antd';
import CsvUploader from '@root/components/shared/CsvUploader';
import { AdminListing } from '@root/types';
import { useTranslation } from 'react-i18next';
import routes from '@root/routes';
import { useFlags } from 'launchdarkly-react-client-sdk';
import css from './AdditionalDataSection.module.less';
import AdditionalDataRow from './AdditionalDataRow';

type Props = {
  listing: AdminListing;
  form: any; // dunno about this old antd form type
  // FF Cleanup: amenities-data-tabs - fetchListing will no longer be referenced
  fetchListing: () => void;
};

const AdditionalDataSection = ({ listing, form, fetchListing }: Props) => {
  const { t } = useTranslation();
  const { getFieldDecorator } = form;
  const flags = useFlags();

  const booleanMetadataFields = [
    'electricalRoom',
    'exteriorSignage',
    'interiorSignage',
    'keyCardSpaceEntry',
    'mechanicalRoom',
    'privateOutdoorSpace',
    'sharedPantry',
  ];

  const numericMetadataFields = () => [
    'drinkingFountainCount',
    'passengerElevatorCount',
    'serviceElevatorCount',
    'sharedFloorBathroomStallCount',
    'windowSidesCount',
  ];

  const showSource = amenity =>
    form.getFieldsValue()[amenity] || form.getFieldsValue()[amenity] === 0;

  return (
    <>
      {/* FF Cleanup - amenities-data-tabs - remove .uploader & .uploaderLabel from styles,
       no longer in use */}
      {flags['amenities-data-tabs'] ? null : (
        <>
          <PageHeader title={t('admin:listing.additionalDataTitle')} />
          <div className={css.uploader}>
            <span className={css.uploaderLabel}>{t('admin:listing.importRoomData')}</span>
            <CsvUploader
              route={routes.api.admin.roomMetadataFile}
              additionalParams={{ listing_id: listing.id }}
              successCallback={fetchListing}
            />
          </div>
        </>
      )}
      <>
        {flags['amenities-data-tabs'] ? null : (
          <>
            <AdditionalDataRow
              valueField={
                <Form.Item label={t('admin:listing.buildOutLevel.label')}>
                  {getFieldDecorator('buildOutLevel', {
                    initialValue: listing.buildOutLevel.value,
                  })(
                    <Select data-testid="buildOutLevel" allowClear>
                      <Select.Option value="Low">
                        {t('admin:listing.buildOutLevel.low')}
                      </Select.Option>
                      <Select.Option value="Medium">
                        {t('admin:listing.buildOutLevel.medium')}
                      </Select.Option>
                      <Select.Option value="High">
                        {t('admin:listing.buildOutLevel.high')}
                      </Select.Option>
                    </Select>,
                  )}
                </Form.Item>
              }
              dataSourceField={
                <Form.Item
                  data-testid="buildOutLevelDataSource"
                  label={t('admin:listing.dataSource')}
                >
                  {getFieldDecorator('buildOutLevelDataSource', {
                    initialValue: listing.buildOutLevel.dataSource,
                  })(<Input />)}
                </Form.Item>
              }
              showDataSource={showSource('buildOutLevel')}
            />
            <AdditionalDataRow
              valueField={
                <Form.Item label={t('admin:listing.elevatorLocation.label')}>
                  {getFieldDecorator('elevatorLocation', {
                    initialValue: listing.elevatorLocation.value,
                  })(
                    <Select data-testid="elevatorLocation" allowClear>
                      <Select.Option value="Side">
                        {t('admin:listing.elevatorLocation.side')}
                      </Select.Option>
                      <Select.Option value="Core">
                        {t('admin:listing.elevatorLocation.core')}
                      </Select.Option>
                    </Select>,
                  )}
                </Form.Item>
              }
              dataSourceField={
                <Form.Item
                  data-testid="elevatorLocationDataSource"
                  label={t('admin:listing.dataSource')}
                >
                  {getFieldDecorator('elevatorLocationDataSource', {
                    initialValue: listing.elevatorLocation.dataSource,
                  })(<Input />)}
                </Form.Item>
              }
              showDataSource={showSource('elevatorLocation')}
            />
            <AdditionalDataRow
              valueField={
                <Form.Item label={t('admin:listing.furnished.label')}>
                  {getFieldDecorator('furnished', {
                    initialValue: listing.furnished.value,
                  })(
                    <Select data-testid="furnished" allowClear>
                      <Select.Option value="Yes">{t('common:yes')}</Select.Option>
                      <Select.Option value="No">{t('common:no')}</Select.Option>
                      <Select.Option value="Partial">
                        {t('admin:listing.furnished.partial')}
                      </Select.Option>
                    </Select>,
                  )}
                </Form.Item>
              }
              dataSourceField={
                <Form.Item data-testid="furnishedDataSource" label={t('admin:listing.dataSource')}>
                  {getFieldDecorator('furnishedDataSource', {
                    initialValue: listing.furnished.dataSource,
                  })(<Input />)}
                </Form.Item>
              }
              showDataSource={showSource('furnished')}
            />
          </>
        )}

        {flags['amenities-data-tabs']
          ? null
          : booleanMetadataFields.map(field => (
              <AdditionalDataRow
                key={field}
                valueField={
                  <Form.Item label={t(`admin:listing.${field}`)} key={field}>
                    {getFieldDecorator(field, { initialValue: listing[field]?.value })(
                      <Select data-testid={field} allowClear>
                        <Select.Option value="Yes">{t('common:yes')}</Select.Option>
                        <Select.Option value="No">{t('common:no')}</Select.Option>
                      </Select>,
                    )}
                  </Form.Item>
                }
                dataSourceField={
                  <Form.Item
                    data-testid={`${field}DataSource`}
                    label={t('admin:listing.dataSource')}
                  >
                    {getFieldDecorator(`${field}DataSource`, {
                      initialValue: listing[field]?.dataSource,
                    })(<Input />)}
                  </Form.Item>
                }
                showDataSource={showSource(field)}
              />
            ))}
        {flags['amenities-data-tabs']
          ? null
          : numericMetadataFields().map(field => (
              <AdditionalDataRow
                key={`${field}DataSource`}
                valueField={
                  <Form.Item label={t(`admin:listing.${field}`)}>
                    {getFieldDecorator(field, {
                      initialValue: listing[field]?.value,
                    })(<InputNumber precision={0} min={0} className={css.inputNumber} />)}
                  </Form.Item>
                }
                dataSourceField={
                  <Form.Item
                    data-testid={`${field}DataSource`}
                    label={t('admin:listing.dataSource')}
                  >
                    {getFieldDecorator(`${field}DataSource`, {
                      initialValue: listing[field]?.dataSource,
                    })(<Input />)}
                  </Form.Item>
                }
                showDataSource={showSource(field)}
              />
            ))}
      </>
    </>
  );
};

export default AdditionalDataSection;
